import React from 'react';
import DocumentPage, {
  Section,
  SubSection,
  Paragraph,
  BulletList,
  OrderedList,
  HazardTable,
} from './DocumentPage';
import CalorVesselsNotice from './CalorVesselsNotice';

const PortableCompressorUnitDoc = ({ onReturnHome }) => (
  <DocumentPage
    pageTitle="Transfer of Liquid LPG – RAMS"
    documentHeading="Method Statement: Transfer of Liquid LPG"
    includeDefaultMeta={false}
    meta={[
      ['Company', 'Pressure Vessel Services Limited'],
      ['Document Reference', 'PVS-RAMS-PCU-001'],
      ['Version', '1.0'],
      ['Date of Issue', '21st April 2026'],
      ['Next Review Date', '21st April 2028'],
      ['Review Period', '24 months'],
      ['Prepared by', 'Pressure Vessel Services Limited'],
      ['Approved by', 'Board of Directors'],
      ['Activity', 'Use of the Portable Compressor Unit (PCU) to transfer residual liquid LPG from a customer vessel to a standby vessel prior to examination and preparation'],
      ['Location', 'Pressure Vessel Services Limited premises and, where authorised, customer / site locations'],
    ]}
    acknowledgment={{
      documentName: 'Transfer of Liquid LPG – RAMS',
      documentReference: 'PVS-RAMS-PCU-001',
      processActivity: 'Portable Compressor Unit Operation and Residual Liquid LPG Transfer',
      versionNumber: '1.0',
      dateOfIssue: '21st April 2026',
    }}
    onReturnHome={onReturnHome}
  >
    <CalorVesselsNotice />

    <Section number="1" title="Introduction and Scope">
      <Paragraph>
        This Method Statement details the safe system of work for the use of the Portable
        Compressor Unit (PCU) to transfer residual liquid LPG from a customer vessel into an
        approved standby vessel, either on Pressure Vessel Services Limited (PVS) premises or, where
        authorised in writing, at a customer / site location. The work typically forms the first
        operation on any vessel presenting with residual liquid content prior to vapour removal,
        valve removal, natural venting, and subsequent examination activities covered by the
        Vessel Examination and Preparation Method Statement.
      </Paragraph>
      <Paragraph className="mt-2">
        This document aims to ensure compliance with the Health and Safety at Work Act 1974, the
        Pressure Systems Safety Regulations 2000 (PSSR), the Dangerous Substances and Explosive
        Atmospheres Regulations 2002 (DSEAR), the Provision and Use of Work Equipment Regulations
        1998 (PUWER), the Lifting Operations and Lifting Equipment Regulations 1998 (LOLER) in
        respect of hose / adaptor handling, and all other relevant UK legislation and the PVS
        Written Scheme of Examination (WSE Section 6 and Appendix 6). It shall be read in
        conjunction with the Vessel Examination and Preparation Method Statement and all
        subsidiary Safe Systems of Work.
      </Paragraph>
    </Section>

    <Section number="2" title="General Information">
      <BulletList
        items={[
          <><strong>Company:</strong> Pressure Vessel Services Limited</>,
          <><strong>Activity:</strong> Transfer of residual liquid LPG from a customer vessel to a standby vessel using the Portable Compressor Unit (PCU), including all preparation, connection, operation, disconnection and making-safe steps.</>,
          <><strong>Personnel:</strong> Only PVS personnel who hold a current written PCU authorisation shall operate the PCU. A second competent person is required as banksman / standby during every live transfer.</>,
          <><strong>Reference Documents:</strong>
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li>Written Scheme of Examination – Section 6 and Appendix 6 (Removal of Residual Liquid LPG)</li>
              <li>Written Scheme of Examination – Section 7 and Section 8 (subsequent vapour removal and valve removal steps)</li>
              <li>PVS Health and Safety Policy</li>
              <li>PCU Manufacturer&apos;s Operating and Maintenance Manual</li>
              <li>PVS-RAMS-VEP-001 – Vessel Inspection and Preparation (follow-on activity)</li>
              <li>LPGA Code of Practice 1 (Bulk LPG Storage at Fixed Installations)</li>
            </ul>
          </>,
        ]}
      />
    </Section>

    <Section number="3" title="Responsibilities">
      <Paragraph className="mb-2"><strong>Overall Responsibility:</strong> The Board of Directors of the Employer.</Paragraph>
      <Paragraph className="mb-4"><strong>Day-to-Day Responsibility:</strong> Health &amp; Safety Officer / Senior Engineer on duty, supported by the nominated PCU Operator.</Paragraph>
      <Paragraph className="font-semibold mb-2">All Staff Involved Must:</Paragraph>
      <BulletList
        items={[
          'Take reasonable care for their own health and safety and that of others who may be affected by their acts or omissions.',
          'Operate the PCU strictly in accordance with the written authorisation held and within the operational limits defined in this Method Statement and the manufacturer\u2019s manual.',
          'Stop work and make safe if any abnormality, leak, smell of gas, unusual noise, or equipment defect is identified, and notify the Senior Engineer without delay.',
          'Complete the pre-use checks and record them on the PCU Daily Record Check Form before each transfer.',
          'Use, inspect and store all hoses, adaptors and couplings in accordance with PVS procedures and the manufacturer\u2019s instructions.',
          'Co-operate with any incident investigation and with the PVS impartiality, confidentiality and competence framework.',
        ]}
      />
    </Section>

    <Section number="4" title="Hazard Identification and Risk Assessment">
      <Paragraph className="mb-2">
        The following assessment applies to the use of the PCU for liquid LPG transfer. Severity is
        rated prior to control measures; residual risk is the rating after the listed controls are
        implemented. All residual risks are controlled to a tolerable level; no task shall
        proceed unless all listed controls are in place.
      </Paragraph>
      <HazardTable
        columns={[
          'Hazard',
          'Who May Be Harmed / Risk',
          'Initial Severity',
          'Control Measures',
          'Residual Risk',
        ]}
        rows={[
          [
            'Uncontrolled release of liquid / vapour LPG (flammable)',
            'Operator, nearby staff, site personnel, members of the public in event of major release – fire, flash fire, explosion, thermal injury, fatality, environmental release.',
            'High',
            [
              'Only the authorised PCU Operator may open / close adaptors and hose valves.',
              'Dry powder fire extinguishers positioned within arm\u2019s reach before any transfer begins.',
              'Approved adaptors and hoses only, correctly fitted and isolated before opening vessel valves.',
              'Leak detection fluid applied to every connection prior to proceeding; no gas escape tolerated.',
              'Controlled, sequenced opening of valves in the order described in Section 5; bleed screws used to vent trapped gas in a safe, controlled manner.',
              'No naked flames, smoking, phones, or ignition sources within the designated exclusion zone (minimum 3 m around the operation, extended as directed by the Senior Engineer).',
              'Suitable safety signage displayed; the work area cordoned off.',
              'On any smell / hiss / frost indicating leakage: immediately close nearest upstream valve, isolate compressor, evacuate and re-assess.',
            ],
            'Low',
          ],
          [
            'High pressure (hoses, fittings, receiving vessel)',
            'Operator and bystanders – whip injury, rupture of hose, pressure injection, over-pressurising receiving vessel, uncontrolled release.',
            'High',
            [
              'PCU used within the manufacturer\u2019s rated pressure; maximum 1 hour continuous running as stated in the operating manual.',
              'Hoses, adaptors and fittings inspected before use for damage, kinks, crimp damage, corrosion and within-date certification where applicable.',
              'Pressure gauges calibrated and legible; readings monitored throughout the transfer.',
              'Receiving vessel water capacity and current fuel level confirmed before transfer; calculations made to prevent overfilling (maximum fill ratio per LPGA CoP 1).',
              'Bypass valve opened to equalise before compressor start; bypass only closed once pressures are settled.',
              'Bleed valve on the liquid transfer hose used to release trapped gas before disconnection.',
            ],
            'Low',
          ],
          [
            'Liquid off-take valve failure / removal',
            'Operator – high-velocity release of liquid LPG, serious cryogenic / thermal injury.',
            'High',
            [
              'Under no circumstances shall a liquid top off-take valve be removed in the field to transfer liquid LPG.',
              'Liquid off-take valve confirmed serviceable and isolated before adaptor fitment; adaptor correctness verified against vessel type.',
              'Where a liquid valve is found defective, the vessel shall be tagged, the Senior Engineer informed, and the transfer aborted until authorised further action is agreed.',
            ],
            'Low',
          ],
          [
            'Cryogenic burns from escaping liquid LPG',
            'Operator, banksman – cold burns, frost bite, skin damage, eye injury.',
            'Medium',
            [
              'Chemical-resistant / cryogenic-rated gloves and face protection worn whenever hoses are opened, bled or disconnected.',
              'Operator positioned to the side of, not in line with, hose ends / bleed outlets.',
              'Bleed screws operated slowly and in short steps; never fully opened in one action.',
            ],
            'Low',
          ],
          [
            'Equipment failure (compressor, flame arrestor, hose, gauge, adaptor)',
            'Operator – release, overpressure, inaccurate readings, ignition via compressor.',
            'High',
            [
              'Daily pre-use inspection of PCU (oil level, flame arrestor cleanliness, belts / guards, hoses, fittings); results recorded.',
              'Flame arrestor regularly disconnected and carbon deposits removed to the schedule in the manufacturer\u2019s manual.',
              'Oil level monitored throughout operation; topped up in accordance with manufacturer\u2019s specification; maximum 1 hour continuous running.',
              'Any fault, damage or abnormal noise – the unit is shut down immediately and taken out of service pending inspection by a competent person.',
              'Hoses, adaptors and gauges are replaced to the manufacturer / supplier schedule; out-of-date or damaged items are condemned and destroyed to prevent re-use.',
            ],
            'Low',
          ],
          [
            'Overfilling of receiving (standby) vessel',
            'Operator, site – hydraulic overfill, PRV lift, uncontrolled release, structural damage.',
            'High',
            [
              'Receiving vessel water capacity, existing fuel level (via float gauge) and calculated headspace recorded before transfer begins.',
              'Fill valve opened slowly under restriction, receiving vessel pressurised progressively; operator confirms flow via bleed screw observation.',
              'Transfer stopped immediately if the receiving vessel float gauge approaches the maximum fill indication.',
              'Where available, fixed maximum fill limiter on the receiving vessel is used as a secondary control; not relied upon as a primary control.',
            ],
            'Low',
          ],
          [
            'Ignition sources in the transfer area (electrical, hot work, vehicles, mobile phones, portable electrical tools)',
            'All persons in area – ignition of flammable atmosphere, fire / explosion.',
            'High',
            [
              'DSEAR hazardous zone maintained around the PCU and vessels in use; only DSEAR-suitable equipment permitted inside the zone.',
              'No mobile phones, cameras or non-ATEX portable electronics within the hazardous zone during live transfer.',
              'No hot work, welding, grinding or smoking within the hazardous zone; hot-work permit required for adjacent work.',
              'Vehicles: engines off, keys removed; no vehicle movements within the zone during transfer.',
              'Earth / bonding strap fitted between PCU and vessel being transferred where provided.',
            ],
            'Low',
          ],
          [
            'Hose / adaptor mechanical failure, trip hazards, handling injury',
            'Operator, passing staff – whip, strike, trip, manual-handling injury.',
            'Medium',
            [
              'Hose routes planned to avoid trafficked paths; matting / cable covers used where hoses cross walkways.',
              'Hoses laid out without kinks, twists or sharp bends; minimum bend radius respected.',
              'Two-person lift for any adaptor / hose assembly that presents a manual-handling risk.',
            ],
            'Low',
          ],
          [
            'Noise (compressor operation)',
            'Operator, banksman, nearby staff – hearing damage over extended exposure.',
            'Medium',
            [
              'Hearing protection (ear defenders / plugs) worn whenever the PCU is running.',
              'PCU positioned to minimise reflected noise where practicable.',
              'Personnel exposure time limited to that required for the transfer task.',
            ],
            'Low',
          ],
          [
            'Environmental release of LPG / compressor oil / spent flame-arrestor deposits',
            'Environment – ground contamination, air emissions.',
            'Medium',
            [
              'Drip trays positioned under PCU during maintenance / oil top-up.',
              'Flame-arrestor carbon deposits collected and disposed of as hazardous waste via the PVS waste stream.',
              'Any suspected environmental release reported to the Senior Engineer and logged in the environmental incident register.',
            ],
            'Low',
          ],
          [
            'Lack of competence / incorrect procedure',
            'Operator, bystanders – mis-sequenced valve operation leading to any of the above releases.',
            'High',
            [
              'Only personnel holding a current written PCU authorisation shall operate the unit.',
              'All operators are trained on this Method Statement, the WSE and the PCU manufacturer\u2019s manual, with periodic refresher training.',
              'Every live transfer is supervised by a Senior Engineer or carried out by two authorised persons (operator + banksman).',
              'Competence records maintained in the PVS Master Training & Competency Matrix.',
            ],
            'Low',
          ],
          [
            'Emergency / incident during transfer',
            'Operator, bystanders – delayed response, escalation of event.',
            'High',
            [
              'Emergency stop on PCU tested as part of the pre-use check.',
              'Clear evacuation route maintained; wind direction checked and exclusion zone positioned upwind of release points where possible.',
              'Emergency procedures briefed before each task (see Section 8).',
              'Dry powder fire extinguishers (minimum 2) within immediate reach; first-aid facility location known to all present.',
            ],
            'Low',
          ],
        ]}
      />
    </Section>

    <Section number="5" title="Safe System of Work (SSoW) – PCU Operation and Liquid LPG Transfer">
      <Paragraph>
        The sequence below mirrors and expands on the requirements of the Written Scheme of
        Examination, Section 6 and Appendix 6. Each step must be completed in order; a step shall
        not be started until the previous step has been verified.
      </Paragraph>

      <SubSection title="5.1 Pre-Task Planning and Area Preparation">
        <OrderedList
          items={[
            'Confirm that a task-specific risk assessment has been completed and briefed to all personnel involved.',
            'Confirm the operator holds a current written PCU authorisation; confirm the presence of a competent banksman / second authorised person.',
            'Obtain customer permission (verbal or non-verbal), issue a Work Permit where required, and confirm the work is within the scope of the Written Scheme of Examination.',
            'Display suitable safety signage; cordon off the designated hazardous zone (DSEAR) and ensure all unauthorised persons are excluded.',
            'Position a minimum of two dry-powder fire extinguishers within arm\u2019s reach before any connections are made.',
            'Confirm weather conditions (wind direction, precipitation, ambient temperature) are suitable; note wind direction for venting operations.',
            'Confirm all vessel service pipework has been removed / disconnected, and all vessel valves are isolated prior to connection.',
          ]}
        />
      </SubSection>

      <SubSection title="5.2 PCU Pre-Use Checks">
        <OrderedList
          items={[
            'Visually inspect the PCU for damage, corrosion or leakage; check all guards are in place.',
            'Check compressor oil level; top up to the manufacturer\u2019s specified level using the correct grade of oil.',
            'Inspect the flame arrestor; if required by the schedule, disconnect and remove carbon deposits before use.',
            'Inspect all hoses, adaptors, fittings, valves and couplings for damage, wear, kinks or contamination; verify in-date where applicable.',
            'Verify pressure gauges read zero when isolated, and that their calibration stickers are in date.',
            'Test the emergency stop; confirm it de-energises the unit.',
            'Record the outcome of the pre-use check on the PCU Daily Record Check Form before use.',
          ]}
        />
      </SubSection>

      <SubSection title="5.3 Receiving (Standby) Vessel Confirmation">
        <OrderedList
          items={[
            'Confirm the receiving vessel is certified, in-date and approved for the product being transferred.',
            'Record the receiving vessel water capacity and current float-gauge reading.',
            'Calculate the available headspace, allowing for the maximum fill ratio in accordance with LPGA Code of Practice 1, to prevent overfilling.',
            'Verify the receiving vessel\u2019s fill valve, vapour valve and bleed arrangements are serviceable.',
          ]}
        />
      </SubSection>

      <SubSection title="5.4 Liquid Line Connection">
        <OrderedList
          items={[
            'Under no circumstances shall a liquid top off-take valve be removed in the field to transfer liquid LPG.',
            'Identify the correct approved adaptor for the liquid off-take on the customer vessel; confirm it is correctly isolated before use.',
            'Fit the approved liquid off-take adaptor, ensuring proper engagement with no cross-threading.',
            'Connect the liquid-transfer hose from the liquid off-take adaptor to the fill valve of the receiving vessel, with all hose and vessel valves in the closed position.',
            'Confirm a bleed valve is fitted on the connection to the vessel being filled.',
            'Open the liquid off-take adaptor first and verify no gas escape; apply leak detection fluid as a secondary confirmation.',
            'Open the bleed screw on the hose slightly and vent until liquid appears at the bleed; close the bleed screw immediately once liquid is observed.',
            'Open the liquid-transfer hose valve slightly to allow a small liquid flow; assess for any gas escape.',
            'Open the hose valve on the fill-point side (leaving the fill valve itself isolated).',
            'Verify no gas escape from the fill-point ACME joint.',
            'Open the hose valves and the liquid off-take valve fully.',
            'Open the fill valve slowly, restricting flow until the receiving vessel is pressurised.',
          ]}
        />
      </SubSection>

      <SubSection title="5.5 Vapour Line Connection (Compressor Side)">
        <OrderedList
          items={[
            'Connect the suction and discharge (vapour) hoses to the compressor with all hose valves closed.',
            'Connect the PCU suction hose to the standby (receiving) vessel vapour valve, using the correct ACME / POL adaptor where applicable.',
            'Connect the PCU discharge hose to the customer vessel vapour valve, using the correct ACME / POL adaptor where applicable.',
            'Open both vapour valves; check for leaks at every joint with leak detection fluid.',
            'Open the hose valves on the compressor and check the PCU ACME connections for gas escape.',
            'Open the bypass valve (inline) on the compressor to allow the vessels to equalise.',
          ]}
        />
      </SubSection>

      <SubSection title="5.6 Transfer Operation">
        <OrderedList
          items={[
            'Confirm exclusion zone and wind direction; brief the banksman on the emergency signal and evacuation route.',
            'Start the compressor.',
            'Close the bypass valve (across) to begin active transfer.',
            'Continuously monitor the site, hose routing, pressure gauges and both vessel float gauges throughout the operation.',
            'Continue the transfer until the liquid has been fully transferred, confirmed by checking the bleed screw on the hose end at the standby vessel – only vapour shall be present.',
            'Observe the maximum continuous run time of the PCU (1 hour) as stated in the manufacturer\u2019s manual; if additional running is required, pause and follow the cooling / re-check procedure before restarting.',
          ]}
        />
      </SubSection>

      <SubSection title="5.7 Controlled Shutdown and Disconnection">
        <OrderedList
          items={[
            'Turn off the compressor.',
            'Isolate the fill valve, liquid-transfer hose valves and the liquid off-take valve.',
            'Isolate both vapour service valves.',
            'Isolate all valves on the discharge and suction hoses.',
            'Open the bleed screw on the liquid-transfer hose and vent trapped gas in a controlled and SAFE manner; operator positioned clear of the vent direction.',
            'Disconnect the vapour hoses and vent off in a controlled manner.',
            'Remove the liquid off-take adaptor.',
            'Isolate all valves and fittings on both vessels; plug the customer vessel openings as required.',
            'Wipe down hoses and adaptors; return equipment to the PCU storage location.',
          ]}
        />
      </SubSection>

      <SubSection title="5.8 Records and Handover">
        <BulletList
          items={[
            'Record the transfer event in the PCU Daily Record Check Form, including vessel serial numbers, transfer start / end times, PCU running time, quantity transferred (estimated) and any abnormal observations.',
            'Where a fault is observed on either vessel, raise the appropriate report (e.g. Vessel Failure Report) and inform the Senior Engineer.',
            'On completion, hand over the customer vessel to the next operation (Vapour Removal – WSE Section 7 / PVS-RAMS-VEP-001 Section 5.2) only after confirming the liquid phase is empty.',
          ]}
        />
      </SubSection>
    </Section>

    <Section number="6" title="Personal Protective Equipment (PPE)">
      <Paragraph className="mb-2">
        Appropriate PPE shall be worn at all times when operating the PCU and when making, breaking
        or venting connections. As a minimum:
      </Paragraph>
      <BulletList
        items={[
          'Hi-visibility jacket / vest.',
          'Steel-toe, rubber-soled footwear.',
          'Hard hat.',
          'Cryogenic- / chemical-resistant gloves whenever hoses are opened, bled or disconnected; general handling gloves at other times.',
          'Eye / face protection (safety glasses and face shield where splash / release risk exists).',
          'Flame-retardant outer clothing; no synthetic outer layers that could melt on ignition.',
          'Hearing protection whilst the PCU is running.',
          'Respiratory Protective Equipment (RPE) if airborne contaminants are suspected (e.g. during flame-arrestor maintenance).',
        ]}
      />
    </Section>

    <Section number="7" title="Training and Competence">
      <BulletList
        items={[
          'PCU operators shall hold a current written PCU authorisation issued by PVS Management and recorded in the PVS Master Training & Competency Matrix.',
          'Training covers: this Method Statement, the Written Scheme of Examination (Sections 6\u20138 and Appendix 6), the PCU manufacturer\u2019s operating manual, LPG hazards and DSEAR, safe connection / disconnection sequence, emergency response and first-aid awareness.',
          'New operators shall complete a supervised period (minimum of documented practical hours signed off by a Senior Engineer) before independent operation is authorised.',
          'Refresher training shall be provided at intervals not exceeding 24 months, following any incident, following any change to equipment / procedure, or where an operator has not undertaken a PCU transfer for a sustained period.',
          'Authorisations may be withdrawn at any time by the Board of Directors or Senior Engineer if the operator is observed to deviate from this Method Statement.',
        ]}
      />
    </Section>

    <Section number="8" title="Emergency Procedures">
      <BulletList
        items={[
          <><strong>Fire / ignition:</strong> Immediately press the PCU emergency stop; close the nearest upstream valve only if it is safe to do so; evacuate the exclusion zone upwind; raise the fire alarm; call emergency services (999). Only attempt to tackle a small, incipient fire if trained, competent and if escape remains unimpeded.</>,
          <><strong>Uncontrolled LPG release:</strong> Stop work; shut down the PCU; isolate at the nearest safe valve where practicable; evacuate upwind; establish a wider exclusion zone; prohibit all ignition sources; notify the Senior Engineer and, where necessary, the emergency services. Allow the area to disperse before re-approach.</>,
          <><strong>Cryogenic burn / contact with liquid LPG:</strong> Remove affected clothing only if it is not frozen to the skin; rinse the affected area with copious volumes of lukewarm (not hot) water for at least 20 minutes; seek urgent medical attention; record the incident in the Accident Book.</>,
          <><strong>Equipment malfunction:</strong> Press the PCU emergency stop, close all vessel valves, isolate hoses, and report immediately to the Senior Engineer. Do not restart the unit until a competent person has inspected and released it back into service.</>,
          <><strong>Accident / injury:</strong> Promptly report any accident involving personal injury (however trivial) to the Health & Safety Officer for recording in the Accident Book; cooperate with any subsequent investigation. For serious incidents call 999. RIDDOR reports shall be handled by the Health & Safety Officer.</>,
        ]}
      />
    </Section>

    <Section number="9" title="Environmental Considerations">
      <BulletList
        items={[
          'Plan transfers to minimise deliberate venting; use the controlled bleed sequence only to the extent required to make equipment safe.',
          'Drip trays / spill kits available for compressor oil spills; any spill cleaned up immediately and waste disposed of via the PVS controlled waste stream.',
          'Flame-arrestor deposits treated as hazardous waste.',
          'Noise management: operations scheduled to minimise disturbance; hearing protection provided.',
          'Any suspected environmental release to be reported to the Senior Engineer and entered in the environmental incident register.',
        ]}
      />
    </Section>

    <Section number="10" title="Review">
      <Paragraph>
        This Method Statement shall be reviewed by the Board of Directors at intervals not
        exceeding 24 months, and additionally following: any change to the PCU or its ancillary
        equipment; any change to the Written Scheme of Examination affecting residual-liquid
        transfer; any incident, near-miss or non-conformance arising from the activities covered
        by this document; or any material change in legislation or recognised codes of practice.
      </Paragraph>
    </Section>
  </DocumentPage>
);

export default PortableCompressorUnitDoc;
